import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ArrowLeft, Eye, Heart, MessageCircle, Share2, TrendingUp, MousePointerClick, UserPlus, Activity, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { formatNumber, formatPercentage } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function VideoDetails() {
  const { videoId } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [affiliateStats, setAffiliateStats] = useState({ visits: 0, signups: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVideoData();
  }, [videoId]);

  const fetchVideoData = async () => {
    try {
      setLoading(true);
      const [videosRes, metricsRes] = await Promise.all([
        axios.get(`${API}/videos`),
        axios.get(`${API}/videos/${videoId}/metrics`)
      ]);
      
      const videoData = videosRes.data.find(v => v.id === videoId);
      setVideo(videoData);
      setMetrics(metricsRes.data);
      
      // Get affiliate stats
      if (videoData) {
        const eventsRes = await axios.get(`${API}/influencers/${videoData.influencer_name}/locations`);
        const totalVisits = eventsRes.data.reduce((sum, loc) => sum + loc.visits, 0);
        const totalSignups = eventsRes.data.reduce((sum, loc) => sum + loc.signups, 0);
        setAffiliateStats({ visits: totalVisits, signups: totalSignups });
      }
    } catch (error) {
      console.error('Error fetching video data:', error);
      toast.error('Failed to load video details');
    } finally {
      setLoading(false);
    }
  };

  const handleTrackNow = async () => {
    try {
      await axios.post(`${API}/videos/${videoId}/track`);
      toast.success('Tracking initiated');
      setTimeout(fetchVideoData, 3000);
    } catch (error) {
      toast.error('Failed to initiate tracking');
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="video-details-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="text-center py-12" data-testid="video-not-found">
        <p className="text-gray-400">Video not found</p>
        <Button onClick={() => navigate('/')} className="mt-4 bg-blue-600 hover:bg-blue-700">Back to Dashboard</Button>
      </div>
    );
  }

  const engagementRate = video.current_views > 0 
    ? ((video.current_likes + video.current_comments + video.current_shares) / video.current_views * 100).toFixed(2)
    : 0;

  const getStatusColor = (status) => {
    if (status === 'ACTIVE') return 'text-green-400';
    if (status === 'DECLINING') return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className="space-y-6" data-testid="video-details-container">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            data-testid="back-button"
            className="border-white/20 text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white" data-testid="video-title">Video Details</h1>
            <p className="text-gray-400 mt-1">{video.influencer_name} - {video.platform}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={handleTrackNow}
            data-testid="track-now-button"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Activity className="w-4 h-4 mr-2" />
            Track Now
          </Button>
          <Button
            onClick={() => navigate(`/influencer/${video.influencer_name}/performance`)}
            data-testid="analysis-button"
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            <BarChart3 className="w-4 h-4 mr-2" />
            Analysis
          </Button>
        </div>
      </div>

      {/* Current Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card rounded-xl p-6" data-testid="current-views-stat">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Current Views</p>
              <p className="text-2xl font-bold text-white mt-1">{formatNumber(video.current_views)}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Eye className="w-5 h-5 text-blue-400" />
            </div>
          </div>
        </div>

        <div className="glass-card rounded-xl p-6" data-testid="current-likes-stat">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Current Likes</p>
              <p className="text-2xl font-bold text-white mt-1">{formatNumber(video.current_likes)}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-pink-500/20 flex items-center justify-center">
              <Heart className="w-5 h-5 text-pink-400" />
            </div>
          </div>
        </div>

        <div className="glass-card rounded-xl p-6" data-testid="current-comments-stat">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Current Comments</p>
              <p className="text-2xl font-bold text-white mt-1">{formatNumber(video.current_comments)}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-purple-400" />
            </div>
          </div>
        </div>

        <div className="glass-card rounded-xl p-6" data-testid="current-shares-stat">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm">Current Shares</p>
              <p className="text-2xl font-bold text-white mt-1">{formatNumber(video.current_shares)}</p>
            </div>
            <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-green-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Status & Engagement */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="glass-card rounded-xl p-6" data-testid="video-status-card">
          <p className="text-gray-400 text-sm mb-2">Status</p>
          <p className={`text-2xl font-bold ${getStatusColor(video.status)}`}>
            {video.status === 'ACTIVE' && '🟢 ACTIVE'}
            {video.status === 'DECLINING' && '🟡 DECLINING'}
            {video.status === 'DEAD' && '🔴 DEAD'}
          </p>
          <p className="text-xs text-gray-500 mt-2">Tracking every {video.tracking_frequency} mins</p>
        </div>

        <div className="glass-card rounded-xl p-6" data-testid="engagement-rate-card">
          <p className="text-gray-400 text-sm mb-2">Engagement Rate</p>
          <p className="text-2xl font-bold text-white">{formatPercentage(engagementRate)}</p>
          <p className="text-xs text-gray-500 mt-2">(likes + comments + shares) / views</p>
        </div>

        <div className="glass-card rounded-xl p-6" data-testid="affiliate-performance-card">
          <p className="text-gray-400 text-sm mb-2">Affiliate Performance</p>
          <div className="flex items-center space-x-4 mt-2">
            <div>
              <p className="text-sm text-gray-400">Visits</p>
              <p className="text-xl font-bold text-cyan-400">{formatNumber(affiliateStats.visits)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400">Signups</p>
              <p className="text-xl font-bold text-green-400">{formatNumber(affiliateStats.signups)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Time-Series Chart */}
      {metrics.length > 0 && (
        <div className="glass-card rounded-xl p-6" data-testid="metrics-chart">
          <h2 className="text-xl font-semibold text-white mb-4">Performance Over Time</h2>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={metrics}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="timestamp" 
                stroke="#9ca3af"
                tickFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                labelStyle={{ color: '#f3f4f6' }}
                formatter={(value) => formatNumber(value)}
              />
              <Legend />
              <Line type="monotone" dataKey="view_count" stroke="#3b82f6" name="Views" strokeWidth={2} />
              <Line type="monotone" dataKey="like_count" stroke="#ec4899" name="Likes" strokeWidth={2} />
              <Line type="monotone" dataKey="comment_count" stroke="#8b5cf6" name="Comments" strokeWidth={2} />
              <Line type="monotone" dataKey="share_count" stroke="#10b981" name="Shares" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Video Info */}
      <div className="glass-card rounded-xl p-6" data-testid="video-info-card">
        <h2 className="text-xl font-semibold text-white mb-4">Video Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-400">Content URL</p>
            <a href={video.content_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 break-all">
              {video.content_url}
            </a>
          </div>
          <div>
            <p className="text-gray-400">Affiliate Link</p>
            <a href={video.affiliate_link} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 break-all">
              {video.affiliate_link}
            </a>
          </div>
          <div>
            <p className="text-gray-400">Go Live Date</p>
            <p className="text-white">{new Date(video.go_live_timestamp).toLocaleString()}</p>
          </div>
          <div>
            <p className="text-gray-400">Last Tracked</p>
            <p className="text-white">
              {video.last_tracked_at ? new Date(video.last_tracked_at).toLocaleString() : 'Never'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoDetails;
